/**
 * Polish.
 *
 * Keyed on the English sentence — see `i18n.tsx` for why. Anything missing here
 * renders in English, so this file can grow without ever leaving the app in a
 * broken state.
 *
 * Two decisions worth stating, because they are the ones a later translator
 * will otherwise reverse by accident:
 *
 * The register is informal throughout ("ty", not "Pan/Pani"). This is a place
 * where people put holiday photographs for their friends, and the formal
 * register would make it sound like a bank.
 *
 * Established loanwords are kept where Polish speakers actually use them —
 * "album", "e-mail", "link" — rather than replaced with correct-but-unused
 * native coinages. The test is what somebody would say out loud, not what a
 * dictionary prefers.
 */

export const pl: Readonly<Record<string, string>> = {
  /* ── Navigation and structure ─────────────────────────────────────────── */
  "Main navigation": "Nawigacja główna",
  Library: "Biblioteka",
  "All photos": "Wszystkie zdjęcia",
  Photos: "Zdjęcia",
  Videos: "Filmy",
  Albums: "Albumy",
  Favourites: "Ulubione",
  Group: "Grupa",
  Groups: "Grupy",
  Wall: "Tablica",
  Trips: "Wyjazdy",
  Settings: "Ustawienia",
  Admin: "Administracja",
  Home: "Start",
  "Collapse sidebar": "Zwiń panel boczny",
  "Expand sidebar": "Rozwiń panel boczny",
  "Sign out": "Wyloguj się",
  "Your account": "Twoje konto",

  /* ── Home ─────────────────────────────────────────────────────────────── */
  "Good morning": "Dzień dobry",
  "Good afternoon": "Dzień dobry",
  "Good evening": "Dobry wieczór",
  "Pick up where you left off.": "Wróć tam, gdzie skończyłeś.",
  "Your groups will appear here.": "Tutaj pojawią się twoje grupy.",
  "Search groups": "Szukaj grup",
  "Clear search": "Wyczyść wyszukiwanie",
  Pinned: "Przypięte",
  "All groups": "Wszystkie grupy",
  "No groups yet": "Nie masz jeszcze żadnej grupy",
  "Create a group": "Załóż grupę",
  "Create new group": "Załóż nową grupę",
  "New group": "Nowa grupa",
  "Join with a code": "Dołącz przy pomocy kodu",

  /* ── Joining ──────────────────────────────────────────────────────────── */
  "Join a group": "Dołącz do grupy",
  "Join this group?": "Dołączyć do tej grupy?",
  Join: "Dołącz",
  "Access code": "Kod dostępu",
  "From whoever invited you. Case and spaces do not matter.":
    "Od osoby, która cię zaprosiła. Wielkość liter i spacje nie mają znaczenia.",
  "That code is not valid. Check it and try again.":
    "Ten kod jest nieprawidłowy. Sprawdź go i spróbuj ponownie.",
  "Everything shared in this group is visible to everyone in it, and to nobody else.":
    "Wszystko, co udostępniasz w tej grupie, widzą tylko jej członkowie i nikt więcej.",

  /* ── Signing in ───────────────────────────────────────────────────────── */
  "Sign in": "Zaloguj się",
  "Username or email": "Nazwa użytkownika lub e-mail",
  Username: "Nazwa użytkownika",
  Password: "Hasło",
  "Your password": "Twoje hasło",
  "Current password": "Obecne hasło",
  "New password": "Nowe hasło",
  "Authenticator code": "Kod z aplikacji uwierzytelniającej",
  "Authentication code": "Kod uwierzytelniający",
  "Open your authenticator app and enter the current 6-digit code.":
    "Otwórz aplikację uwierzytelniającą i wpisz aktualny 6-cyfrowy kod.",
  "MyFriends.Cloud is invite-only for now.":
    "MyFriends.Cloud działa na razie tylko z zaproszeniem.",
  "Have an access code?": "Masz kod dostępu?",
  "One private place for all the memories your friends share.":
    "Jedno prywatne miejsce na wszystkie wspomnienia, którymi dzielisz się ze znajomymi.",
  "First name": "Imię",
  "Last name": "Nazwisko",
  "Date of birth": "Data urodzenia",
  "Choose a username.": "Wybierz nazwę użytkownika.",
  "Use at least 10 characters.": "Użyj co najmniej 10 znaków.",
  "At least 10 characters. A phrase you will remember beats a short puzzle.":
    "Co najmniej 10 znaków. Zdanie, które zapamiętasz, jest lepsze niż krótka łamigłówka.",
  "That did not work.": "Nie udało się.",

  /* ── Locked photos ────────────────────────────────────────────────────── */
  "Your photos are locked": "Twoje zdjęcia są zablokowane",
  "Unlock your photos": "Odblokuj swoje zdjęcia",
  Unlock: "Odblokuj",
  "That did not open the keyring. Check the password and try again.":
    "To nie otworzyło twoich kluczy. Sprawdź hasło i spróbuj ponownie.",

  /* ── Email confirmation ───────────────────────────────────────────────── */
  "Confirm your email": "Potwierdź swój e-mail",
  "Send it again": "Wyślij ponownie",
  "Change it": "Zmień go",
  "If that address can receive mail, a link is on its way.":
    "Jeśli ten adres może odbierać pocztę, link jest już w drodze.",
  "We send a link to confirm it.": "Wyślemy link, żeby go potwierdzić.",

  /* ── Gallery ──────────────────────────────────────────────────────────── */
  "No photos yet": "Nie ma jeszcze zdjęć",
  "Nothing matches those filters": "Nic nie pasuje do tych filtrów",
  "Try widening the date range, or clear the filters to see everything.":
    "Poszerz zakres dat albo wyczyść filtry, żeby zobaczyć wszystko.",
  "Clear filters": "Wyczyść filtry",
  "Add photos": "Dodaj zdjęcia",
  "Start building your trip memories. Drag photos anywhere on this page, or use the button above.":
    "Zacznij zbierać wspomnienia z wyjazdu. Przeciągnij zdjęcia w dowolne miejsce tej strony albo użyj przycisku powyżej.",
  "Media type": "Rodzaj",
  All: "Wszystko",
  "Added by": "Dodane przez",
  Anyone: "Ktokolwiek",
  Year: "Rok",
  "Any year": "Dowolny rok",
  "Group by": "Grupuj według",
  "Group by month": "Grupuj według miesiąca",
  "Group by album": "Grupuj według albumu",
  "Sort by": "Sortuj według",
  "Newest first": "Najnowsze najpierw",
  "Oldest first": "Najstarsze najpierw",
  "Recently added": "Ostatnio dodane",
  "Photo size": "Wielkość kafelków",
  "Small tiles": "Małe kafelki",
  "Medium tiles": "Średnie kafelki",
  "Large tiles": "Duże kafelki",
  Largest: "Największe",
  "Not in an album": "Poza albumem",
  "No preview": "Brak podglądu",
  Play: "Odtwórz",
  People: "Osoby",
  "Find duplicates": "Znajdź duplikaty",

  /* ── Albums ───────────────────────────────────────────────────────────── */
  "New album": "Nowy album",
  "Edit album": "Edytuj album",
  "Create album": "Utwórz album",
  "All albums": "Wszystkie albumy",
  Album: "Album",
  "A place for one trip or occasion. Only the name is required.":
    "Miejsce na jeden wyjazd albo jedną okazję. Wymagana jest tylko nazwa.",
  Description: "Opis",
  "Optional. A line about what this album holds.":
    "Opcjonalnie. Jedno zdanie o tym, co jest w tym albumie.",
  Starts: "Początek",
  Ends: "Koniec",
  Location: "Miejsce",
  "Optional. Where this happened.": "Opcjonalnie. Gdzie to się działo.",
  "The end date is before the start date.":
    "Data końcowa jest wcześniejsza niż początkowa.",
  "There's already something with that name here.":
    "Coś o tej nazwie już tu jest.",
  "These would sit nicely in an album.":
    "Te zdjęcia dobrze wyglądałyby w albumie.",
  "Create one": "Utwórz",

  /* ── Identity picker ──────────────────────────────────────────────────── */
  Name: "Nazwa",
  Colour: "Kolor",
  Emoji: "Emoji",
  "No emoji": "Bez emoji",
  "No colour": "Bez koloru",
  "Search — a country, a flag, a mood": "Szukaj — kraj, flaga, nastrój",
  "Search emoji": "Szukaj emoji",
  "Follow the group": "Jak grupa",

  /* ── People ───────────────────────────────────────────────────────────── */
  Unnamed: "Bez nazwiska",
  "Add a name": "Dodaj imię",
  "Who is this?": "Kto to jest?",
  "Who are these two?": "Kim są te dwie osoby?",
  "No faces found yet.": "Nie znaleziono jeszcze żadnych twarzy.",
  "Nobody has been recognised in more than one photo yet.":
    "Nikt nie został jeszcze rozpoznany na więcej niż jednym zdjęciu.",
  "Stop showing this person? Nothing is deleted.":
    "Przestać pokazywać tę osobę? Nic nie zostanie usunięte.",
  "Not a person / remove": "To nie osoba / usuń",
  Hide: "Ukryj",

  /* ── Uploading ────────────────────────────────────────────────────────── */
  "Upload progress": "Postęp wysyłania",
  Waiting: "Oczekuje",
  Checking: "Sprawdzanie",
  Preparing: "Przygotowywanie",
  Encrypting: "Szyfrowanie",
  Uploading: "Wysyłanie",
  Finishing: "Kończenie",
  Processing: "Przetwarzanie",
  "Processing…": "Przetwarzanie…",
  Added: "Dodane",
  "Already here": "Już tutaj",
  Failed: "Nie udało się",
  Cancelled: "Anulowane",
  "All done.": "Gotowe.",
  "Tap to see what went wrong.": "Dotknij, żeby zobaczyć, co poszło nie tak.",
  "Uploaded. Building previews — safe to keep browsing.":
    "Wysłane. Tworzymy podglądy — możesz spokojnie przeglądać dalej.",
  "This device ran out of memory while preparing the photo. Try adding fewer at a time.":
    "Temu urządzeniu zabrakło pamięci podczas przygotowywania zdjęcia. Spróbuj dodawać mniej naraz.",
  "The connection dropped during the upload. It will work on a retry.":
    "Połączenie zerwało się w trakcie wysyłania. Ponowna próba powinna się udać.",

  /* ── Wall ─────────────────────────────────────────────────────────────── */
  "Share something with the group…": "Podziel się czymś z grupą…",
  Post: "Opublikuj",
  "Write a post": "Napisz post",
  "No album": "Bez albumu",
  "Add these photos to an album": "Dodaj te zdjęcia do albumu",
  "Waiting for the photos to finish processing":
    "Czekamy, aż zdjęcia skończą się przetwarzać",

  /* ── Account ──────────────────────────────────────────────────────────── */
  "Display name": "Nazwa wyświetlana",
  "What everyone in your groups sees beside your photos.":
    "To widzą obok twoich zdjęć wszyscy w twoich grupach.",
  "Save name": "Zapisz nazwę",
  Email: "E-mail",
  "Email address": "Adres e-mail",
  "Used to sign in and to reach you.":
    "Służy do logowania i do kontaktu z tobą.",
  "This address is not confirmed yet.":
    "Ten adres nie został jeszcze potwierdzony.",
  "Change email": "Zmień e-mail",
  "Confirms it is you, because this address can be used to get back in.":
    "Potwierdza, że to ty — tym adresem można odzyskać dostęp do konta.",
  "Change password": "Zmień hasło",
  "Changing it re-seals the key that opens your photos, so it has to be done from a browser that can already open them.":
    "Zmiana hasła na nowo zabezpiecza klucz otwierający twoje zdjęcia, więc trzeba ją zrobić w przeglądarce, która już potrafi je otworzyć.",
  "Profile picture": "Zdjęcie profilowe",
  "Your name has been updated.": "Twoja nazwa została zaktualizowana.",
  "Your password has been changed.": "Twoje hasło zostało zmienione.",
  "Check the new address for a confirmation link.":
    "Sprawdź nowy adres — wysłaliśmy tam link potwierdzający.",
  "That could not be saved.": "Nie udało się tego zapisać.",
  "That could not be changed.": "Nie udało się tego zmienić.",
  "That is not your current password.": "To nie jest twoje obecne hasło.",
  "This is required.": "To pole jest wymagane.",
  Language: "Język",
  "The language this site is shown in.":
    "Język, w którym wyświetla się ta strona.",

  /* ── Common actions ───────────────────────────────────────────────────── */
  Cancel: "Anuluj",
  Save: "Zapisz",
  "Save changes": "Zapisz zmiany",
  Delete: "Usuń",
  Remove: "Usuń",
  Share: "Udostępnij",
  Download: "Pobierz",
  Close: "Zamknij",
  Continue: "Dalej",
  Back: "Wstecz",
  Retry: "Spróbuj ponownie",
  "Try again": "Spróbuj ponownie",
  Rename: "Zmień nazwę",
  Edit: "Edytuj",
  Loading: "Wczytywanie",
  Optional: "Opcjonalnie",
  Owner: "Właściciel",
  Member: "Członek",
  Storage: "Miejsce",
  "Move to trash": "Przenieś do kosza",
  Trash: "Kosz",
  Restore: "Przywróć",
  Selected: "Zaznaczone",
  "Select all": "Zaznacz wszystko",
  "Clear selection": "Odznacz wszystko",
  "A group is either one you start yourself, or one a friend invites you into. If somebody has sent you a link, open it — if they only gave you a code, type it in here.":
    "Grupę albo zakładasz sam, albo ktoś ze znajomych cię do niej zaprasza. Jeśli dostałeś link — otwórz go. Jeśli dostałeś tylko kod — wpisz go tutaj.",
  Originals: "Oryginały",
  Derivatives: "Podglądy",
  Video: "Wideo",
  Exports: "Eksporty",
  Free: "Wolne",
  "The trash is empty": "Kosz jest pusty",
  "Photos wait here before they are gone for good. Put anything back while it is still listed.":
    "Zdjęcia czekają tutaj, zanim znikną na dobre. Możesz je przywrócić, dopóki są na liście.",
  "Nothing has been deleted here, or everything has already been cleared.":
    "Nic tu nie zostało usunięte albo wszystko już wyczyszczono.",
  "Put back": "Przywróć",
  "Cleared in": "Zniknie za",
  days: "dni",
  "Cleared tomorrow": "Zniknie jutro",
  "Clearing any time now": "Zniknie lada moment",
  "Waiting to be cleared": "Czeka na wyczyszczenie",
  "Show trash": "Pokaż kosz",
  "Shown beside your name everywhere you appear.":
    "Widoczne obok twojego imienia wszędzie, gdzie się pojawiasz.",
  "This one picture is not encrypted, unlike your photos — it has to be readable in every group you are in.":
    "To jedno zdjęcie nie jest szyfrowane, w odróżnieniu od twoich zdjęć — musi być czytelne w każdej grupie, do której należysz.",
  "Choose a picture": "Wybierz zdjęcie",
  "Change picture": "Zmień zdjęcie",
  "Your profile picture has been updated.":
    "Twoje zdjęcie profilowe zostało zaktualizowane.",
  "Same as…": "To ta sama osoba co…",
  "Tap who this is the same as": "Dotknij, kto to ta sama osoba",

  /* ── Home: the album row and the library statistics ───────────────────── */
  "Recent albums": "Ostatnie albumy",
  "Scroll left": "Przewiń w lewo",
  "Scroll right": "Przewiń w prawo",
  "Search groups and albums": "Szukaj grup i albumów",
  "Search your groups and albums": "Szukaj wśród swoich grup i albumów",
  "Your library": "Twoja biblioteka",
  "Photos and videos": "Zdjęcia i filmy",
  "Storage left": "Pozostałe miejsce",
  Countries: "Kraje",
  /* "12 GB z 500 GB" — the preposition, on its own, between two sizes. */
  of: "z",
  "Open one of these.": "Otwórz jedną z nich.",

  /* ── The country an album is about ────────────────────────────────────── */
  Country: "Kraj",
  "Search for a country": "Szukaj kraju",
  "Optional. Counted in your statistics.":
    "Opcjonalnie. Liczy się do twoich statystyk.",
  /* Reads as "Użyć Słowenia?" — the suggestion chip appends the name. */
  Use: "Użyć",
  "Clear country": "Wyczyść kraj",
  "No country": "Bez kraju",

  /* ── Share links, and the manager for them ────────────────────────────── */
  "Shared links": "Udostępnione linki",
  "Anyone holding one of these can open what it points at without an account. Revoking a link closes it for everyone immediately.":
    "Każdy, kto ma taki link, otworzy to, na co wskazuje, bez konta. Unieważnienie linku zamyka go natychmiast dla wszystkich.",
  "Nothing in this group is shared outside it.":
    "Nic z tej grupy nie jest udostępnione na zewnątrz.",
  Revoke: "Unieważnij",
  "Revoke this link?": "Unieważnić ten link?",
  "It stops working straight away, for everyone who has it. There is no way to bring the same link back — you would have to share again and send the new one.":
    "Przestanie działać od razu, dla wszystkich, którzy go mają. Tego samego linku nie da się przywrócić — trzeba udostępnić ponownie i wysłać nowy.",
  "Keep it": "Zostaw",
  "Link revoked. It will not open again.":
    "Link unieważniony. Już się nie otworzy.",
  "It has been opened": "Otwarto go",
  /* Polish counts differently: "1 raz", "2 razy". Both forms are needed. */
  "time.": "raz.",
  "times.": "razy.",
  "It has never been opened.": "Nikt go jeszcze nie otworzył.",
  "An album": "Album",
  "Someone who has left": "Ktoś, kto odszedł",
  Views: "Wyświetlenia",
  Downloads: "Pobrania",
  photo: "zdjęcie",
  photos: "zdjęć",
  password: "hasło",
  "full size": "pełny rozmiar",
  "no downloads": "bez pobierania",
  expired: "wygasł",
  until: "do",

  /* ── Two-factor authentication ────────────────────────────────────────── */
  "Two-factor authentication": "Uwierzytelnianie dwuskładnikowe",
  "An app on your phone generates a six-digit code that changes every thirty seconds. You will be asked for it when you sign in, and before anything destructive.":
    "Aplikacja w twoim telefonie generuje sześciocyfrowy kod, który zmienia się co trzydzieści sekund. Poprosimy o niego przy logowaniu i przed każdą nieodwracalną operacją.",
  "Set up two-factor": "Włącz dwuskładnikowe",
  "Scan this with an authenticator app — Google Authenticator, 1Password, Aegis, whichever you use — then type the code it shows.":
    "Zeskanuj to aplikacją uwierzytelniającą — Google Authenticator, 1Password, Aegis, której używasz — i wpisz kod, który pokaże.",
  "QR code for your authenticator app":
    "Kod QR dla aplikacji uwierzytelniającej",
  "Or type this key in by hand:": "Albo wpisz ten klucz ręcznie:",
  "Copy key": "Kopiuj klucz",
  "Code from your app": "Kod z aplikacji",
  "Turn it on": "Włącz",
  "Turn it off": "Wyłącz",
  "Keep it on": "Zostaw włączone",
  "Enter a current code to turn two-factor off.":
    "Wpisz aktualny kod, żeby wyłączyć dwuskładnikowe.",
  "Two-factor authentication is on for this account.":
    "Uwierzytelnianie dwuskładnikowe jest włączone na tym koncie.",
  "Two-factor authentication is off.":
    "Uwierzytelnianie dwuskładnikowe jest wyłączone.",
  "Save these now. Each one signs you in once if you lose your phone, and this is the only time they are shown — we keep only hashes, so we cannot show them again.":
    "Zapisz je teraz. Każdy z nich zaloguje cię raz, jeśli zgubisz telefon, i pokazujemy je tylko ten jeden raz — trzymamy wyłącznie skróty, więc nie pokażemy ich ponownie.",
  "Copy all": "Kopiuj wszystkie",
  "I have saved them": "Zapisałem je",
  "Copied.": "Skopiowano.",
  "Couldn't copy — select the text and copy it manually.":
    "Nie udało się skopiować — zaznacz tekst i skopiuj ręcznie.",

  /* ── People ───────────────────────────────────────────────────────────── */
  "Same person": "Ta sama osoba",
  "Tap a face": "Dotknij twarzy",

  /* Shown while something is being fetched and has not answered yet. */
  "Looking…": "Szukam…",
  "Working…": "Pracuję…",

  /* Starting the People strip over, when names ended up on the wrong faces. */
  "Start again": "Zacznij od nowa",
  "Forget the names": "Zapomnij imiona",
  "Find the faces again too": "Znajdź też twarze od nowa",
  "If people have the wrong names, or two people ended up as one, you can clear this and name them again.":
    "Jeśli osoby mają złe imiona albo dwie osoby zlały się w jedną, możesz to wyczyścić i nazwać je od nowa.",
  "Everybody becomes Unnamed. The faces stay found, so there is nothing to scan again and this takes a moment.":
    "Wszyscy staną się bez imienia. Twarze zostają znalezione, więc nie ma czego skanować od nowa i zajmie to chwilę.",
  "Also throws away the faces themselves and looks through every photo again. Use this if people are grouped together wrongly. It takes a while.":
    "Usuwa też same twarze i przegląda każde zdjęcie od nowa. Użyj tego, jeśli osoby są źle pogrupowane. To chwilę potrwa.",

  /* Choosing an album before the file picker. */
  "Add to which album?": "Do którego albumu dodać?",
  "Search albums": "Szukaj albumów",
  "Switch group": "Zmień grupę",
  item: "element",
  items: "elementy",

  /* Signing out from the account page, which is the only way on a phone. */
  "You will need your password to sign back in.":
    "Aby zalogować się ponownie, będziesz potrzebować hasła.",

  /* Making sure every group has an offline copy of its key. */
  "Protect your groups": "Zabezpiecz swoje grupy",
  "Protect them": "Zabezpiecz je",
  "All your groups are protected now.":
    "Wszystkie Twoje grupy są teraz zabezpieczone.",
  "One group is not protected yet.":
    "Jedna grupa nie jest jeszcze zabezpieczona.",
  "groups are not protected yet.": "grupy nie są jeszcze zabezpieczone.",
  "There is no recovery key set up yet.":
    "Nie ustawiono jeszcze klucza odzyskiwania.",

  /* The key that identifies you to the rest of the group. */
  "Your public key": "Twój klucz publiczny",
  "Show my public key": "Pokaż mój klucz publiczny",

  /* ══════════════════════ Strona główna ═══════════════════════════════════ */

  /* Nawigacja i sekcje. */
  Why: "Dlaczego",
  "How it works": "Jak to działa",
  Compare: "Porównanie",
  Security: "Bezpieczeństwo",
  Pricing: "Cennik",
  Compared: "Porównanie",
  Questions: "Pytania",
  Apps: "Aplikacje",
  Privacy: "Prywatność",
  Terms: "Regulamin",

  /* Nagłówek. */
  "Every photo from the trip, in one place.":
    "Wszystkie zdjęcia z wyjazdu w jednym miejscu.",
  "A shared library for one group of friends — everybody’s camera roll in a single album, with a wall to talk about it. Encrypted on your own device before any of it is uploaded.":
    "Wspólna biblioteka dla jednej paczki znajomych — zdjęcia z telefonu każdego z was w jednym albumie, plus tablica, żeby o nich pogadać. Wszystko szyfrowane na Twoim urządzeniu, zanim cokolwiek zostanie wysłane.",

  /* Liczby na skróty. */
  "No limit": "Bez limitu",
  "on people in a group": "osób w grupie",
  "on photos — only storage": "zdjęć — liczy się tylko miejsce",
  "Full size": "Pełny rozmiar",
  "originals, never recompressed": "oryginały, nigdy nie kompresowane",
  "free, per group": "za darmo, na grupę",

  /* Jak to działa. */
  "Three steps, then it is out of your way.":
    "Trzy kroki i schodzi Ci z drogi.",
  "Make a group": "Załóż grupę",
  "Or join one with a code. Your browser makes a key pair and locks the private half with your password.":
    "Albo dołącz do istniejącej przez kod. Twoja przeglądarka tworzy parę kluczy i zamyka prywatną połowę Twoim hasłem.",
  "Everyone adds photos": "Każdy wrzuca zdjęcia",
  "Each file is encrypted on the device it came from, before any of it is uploaded. What we store is the encrypted file.":
    "Każdy plik jest szyfrowany na urządzeniu, z którego pochodzi, jeszcze zanim cokolwiek zostanie wysłane. U nas leży zaszyfrowany plik.",
  "It sorts itself out": "Samo się układa",
  "Albums, dates, countries, and the people in the pictures — worked out in your browser, not on a server.":
    "Albumy, daty, kraje i osoby na zdjęciach — wyliczone w Twojej przeglądarce, nie na serwerze.",

  /* Co dostajesz. */
  "Many albums inside one group": "Wiele albumów w jednej grupie",
  "Duplicates caught on upload": "Duplikaty wychwytywane przy wysyłce",
  Slideshow: "Pokaz slajdów",
  "Faces found on your device": "Twarze rozpoznawane na Twoim urządzeniu",
  "Filter by person, album, year, who added it":
    "Filtruj po osobie, albumie, roku i tym, kto dodał",
  "Crop, straighten, colour": "Kadrowanie, prostowanie, kolor",
  "Comments on every photo": "Komentarze pod każdym zdjęciem",
  "Video transcoding": "Konwersja wideo",
  "Trips with countries": "Wyjazdy z krajami",
  "Expiring share links": "Linki, które wygasają",
  "Two-factor sign-in": "Logowanie dwuskładnikowe",
  "Offline recovery key": "Klucz odzyskiwania offline",

  /* Porównanie. */
  "You already have somewhere to put photos.":
    "Masz już gdzie trzymać zdjęcia.",
  "Two of them, in fact, and they are good at different halves of this. Here is where each stops.":
    "Właściwie dwa miejsca, a każde jest dobre w innej połowie tej roboty. Oto gdzie się kończą.",
  "A cloud drive": "Dysk w chmurze",
  "A photo service": "Serwis ze zdjęciami",
  "Keeping the provider out of your photos":
    "Trzymanie dostawcy z dala od Twoich zdjęć",
  "Encrypted in transit and at rest, with a key they hold":
    "Szyfrowane w przesyle i na dysku — kluczem, który mają oni",
  "Same, and they read them to build search and tagging":
    "Tak samo, i czytają je, żeby zbudować wyszukiwanie i tagi",
  "Made on your device, and the file is encrypted before it is uploaded":
    "Powstaje na Twoim urządzeniu, a plik jest szyfrowany przed wysłaniem",
  "Editing, without leaving": "Edycja bez wychodzenia",
  "Download it, edit it elsewhere, upload it again":
    "Pobierz, edytuj gdzie indziej, wyślij z powrotem",
  "Crop and adjust, saved to their servers":
    "Kadrowanie i korekta, zapisywane na ich serwerach",
  "Crop, straighten, exposure, colour — in your browser, original untouched":
    "Kadrowanie, prostowanie, ekspozycja, kolor — w przeglądarce, oryginał nietknięty",
  "Finding a particular person": "Znalezienie konkretnej osoby",
  "A drive does not look at pictures": "Dysk nie zagląda w zdjęcia",
  "Yes — recognised on their servers": "Tak — rozpoznawane na ich serwerach",
  "Recognised by your browser. No face ever reaches us":
    "Rozpoznawane przez Twoją przeglądarkę. Żadna twarz do nas nie trafia",
  "Deciding who in the group can do what": "Kto w grupie co może",
  "View or edit, per file or folder":
    "Podgląd albo edycja, per plik lub folder",
  "Contributor or viewer, per album": "Współtwórca albo obserwator, per album",
  "Three roles and ten switches — who uploads, deletes, invites, shares":
    "Trzy role i dziesięć przełączników — kto wysyła, kasuje, zaprasza, udostępnia",
  "How many photos it holds": "Ile zdjęć pomieści",
  "As many as your plan has room for": "Tyle, ile zmieści się w Twoim planie",
  "A shared album is commonly capped in the thousands":
    "Album współdzielony zwykle ma limit rzędu kilku tysięcy",
  "No count limit at all. The only ceiling is the storage total":
    "Żadnego limitu sztuk. Jedyny sufit to łączne miejsce",
  "How many people can be in it": "Ile osób może w niej być",
  "Per-file sharing, one person at a time":
    "Udostępnianie plik po pliku, osoba po osobie",
  "A capped list of subscribers": "Lista subskrybentów z limitem",
  "No limit, and any of them can be given the invite":
    "Bez limitu, i każdej z nich można dać prawo do zapraszania",
  "Albums inside the shared space": "Albumy wewnątrz wspólnego miejsca",
  "Folders, as many as you like": "Foldery, ile tylko chcesz",
  "A shared album is one flat album. That is the whole unit":
    "Album współdzielony to jeden płaski album. I to cała jednostka",
  "As many albums as the trip needs, all inside one group":
    "Tyle albumów, ile potrzebuje wyjazd, wszystkie w jednej grupie",
  "Clearing out duplicates": "Sprzątanie duplikatów",
  "Identical filenames at best": "Co najwyżej identyczne nazwy plików",
  "Nothing stops the same photo going in twice":
    "Nic nie powstrzyma tego samego zdjęcia przed wejściem dwa razy",
  "Caught on upload, and near-identical shots found later, side by side":
    "Wychwycone przy wysyłce, a niemal identyczne ujęcia znalezione później — obok siebie",
  "Sharing with somebody outside": "Udostępnianie komuś z zewnątrz",
  "A link anybody who finds it can open":
    "Link, który otworzy każdy, kto go znajdzie",
  "A link that carries its own key, expires, and can be revoked":
    "Link, który niesie własny klucz, wygasa i można go unieważnić",
  "Getting everything back out": "Wyciągnięcie wszystkiego z powrotem",
  "It is a drive. Drag it out": "To dysk. Przeciągnij i masz",
  "An export you request and wait for": "Eksport, który zamawiasz i czekasz",
  "Download the originals whenever you like":
    "Pobierz oryginały, kiedy tylko chcesz",

  /* Bezpieczeństwo. */
  "Encrypted before it leaves your device.":
    "Szyfrowane, zanim opuści Twoje urządzenie.",
  "Not a policy we could quietly change — an arrangement where the server is never handed anything that opens a photograph.":
    "To nie regulamin, który moglibyśmy po cichu zmienić — to układ, w którym serwer nigdy nie dostaje niczego, co otwiera zdjęcie.",
  "Nobody outside your group": "Nikt spoza Twojej grupy",
  "Every photo has its own key, sealed to the group. AES-256-GCM.":
    "Każde zdjęcie ma własny klucz, zapieczętowany dla grupy. AES-256-GCM.",
  "Not us either": "My też nie",
  "The key is made on your device. Files are encrypted there, before they are uploaded.":
    "Klucz powstaje na Twoim urządzeniu. Pliki są tam szyfrowane, zanim zostaną wysłane.",
  "Your password is the key": "Twoje hasło jest kluczem",
  "600,000 rounds of PBKDF2 between the two, and we never see either.":
    "600 000 rund PBKDF2 pomiędzy nimi, a my nie widzimy ani jednego, ani drugiego.",
  "Faces never leave your phone": "Twarze nie opuszczają telefonu",
  "Recognition runs in your browser, on pictures it already has open.":
    "Rozpoznawanie działa w przeglądarce, na zdjęciach, które i tak ma już otwarte.",
  "A link you can take back": "Link, który możesz cofnąć",
  "Its key rides in the part of the URL a server never receives. Expire it whenever.":
    "Jego klucz jedzie w tej części adresu, której serwer nigdy nie dostaje. Wygaś go, kiedy chcesz.",
  "Leave with everything": "Odejdź z wszystkim",
  "Download every original at any time. No queue, nobody to ask.":
    "Pobierz każdy oryginał, kiedy chcesz. Bez kolejki, bez pytania kogokolwiek.",

  /* Aplikacje. */
  "Everyone is invited, whatever they carry.":
    "Zapraszamy wszystkich, niezależnie od tego, co mają w kieszeni.",

  /* Cennik. */
  "Free to try. Free to stay.": "Za darmo na start. Za darmo na stałe.",
  "Storage is the only thing that costs us anything, so it is the only thing we charge for. Every feature is in every plan.":
    "Miejsce to jedyna rzecz, która nas cokolwiek kosztuje, więc tylko za nie bierzemy pieniądze. Każda funkcja jest w każdym planie.",
  "1 GB per group": "1 GB na grupę",
  "2 groups of your own": "2 własne grupy",
  "Join any number of groups": "Dołącz do dowolnej liczby grup",
  "Every feature": "Wszystkie funkcje",
  "10 groups of your own": "10 własnych grup",
  "Longer videos": "Dłuższe filmy",
  "Originals kept forever": "Oryginały trzymane na zawsze",
  "25 groups of your own": "25 własnych grup",
  "Unlimited share links": "Nielimitowane linki",
  "Originals over links": "Oryginały przez linki",
  "Unlimited groups": "Grupy bez limitu",
  "Versioned storage": "Magazyn z wersjami",
  "Everything in Circle": "Wszystko z planu Circle",

  /* Pytania. */
  "The ones people actually ask.": "Te, które ludzie naprawdę zadają.",
  "Why can I not just sign up?":
    "Dlaczego nie mogę się po prostu zarejestrować?",
  "Every group costs storage and every account is somebody we would rather be able to help properly. Invitations go out in batches so the second part stays true.":
    "Każda grupa kosztuje miejsce, a każde konto to człowiek, któremu wolimy móc porządnie pomóc. Zaproszenia wychodzą partiami, żeby ta druga część została prawdą.",
  "What if I forget my password?": "Co, jeśli zapomnę hasła?",
  "If your group has a recovery copy of its key, you can be let back in. If it does not, the photographs stay locked — which is the same property that keeps them private.":
    "Jeśli Twoja grupa ma kopię odzyskiwania swojego klucza, można Cię wpuścić z powrotem. Jeśli nie ma, zdjęcia zostają zamknięte — to ta sama właściwość, która trzyma je prywatnymi.",
  "Can I get my photos out?": "Czy mogę wyciągnąć swoje zdjęcia?",
  "At any time, as the original files, without asking anybody. There is no export queue and nothing to wait for.":
    "W każdej chwili, jako oryginalne pliki, bez pytania kogokolwiek. Nie ma kolejki eksportu ani na co czekać.",
  "What happens when I leave a group?": "Co się dzieje, gdy opuszczam grupę?",
  "You lose access immediately. Anything you uploaded stays with the group unless you delete it first — the others hold keys to it, and it is as much their trip as yours.":
    "Tracisz dostęp natychmiast. To, co wrzuciłeś, zostaje w grupie, chyba że wcześniej to skasujesz — inni mają do tego klucze, a to ich wyjazd tak samo jak Twój.",

  /* Zakończenie i stopka. */
  "There will be room soon.": "Niedługo zrobi się miejsce.",
  "Leave an address and we will write when there is. One email — no newsletter, no product updates, no reminders.":
    "Zostaw adres, a napiszemy, kiedy się zrobi. Jeden e-mail — bez newslettera, bez nowinek produktowych, bez przypomnień.",
  "Encrypted on your devices, before anything is uploaded.":
    "Szyfrowane na Twoich urządzeniach, zanim cokolwiek zostanie wysłane.",

  /* Zapisy na listę oczekujących. */
  "Get an invite": "Zdobądź zaproszenie",
  "Join the list": "Dopisz się",
  "Invite-only while we grow. One email when a place opens, and no other mail.":
    "Tylko z zaproszenia, dopóki rośniemy. Jeden e-mail, gdy zwolni się miejsce, i żadnej innej poczty.",
  "Add your email address and we will let you know.":
    "Podaj swój adres e-mail, a damy Ci znać.",
  "That did not go through. Check your connection and try again in a moment.":
    "Nie udało się wysłać. Sprawdź połączenie i spróbuj za chwilę.",
  "You are on the list. We will write when there is room — nothing else, ever.":
    "Jesteś na liście. Napiszemy, gdy zrobi się miejsce — i nic poza tym, nigdy.",

  /* Podpisy w narysowanej aplikacji. */
  "Search this group": "Szukaj w tej grupie",
  "Nothing here": "Nic tu nie ma",
  "Nothing in the trash. Deleted photos wait 30 days.":
    "Kosz jest pusty. Usunięte zdjęcia czekają 30 dni.",
  "That does not look like an email address.":
    "To nie wygląda na adres e-mail.",

  /* Ściana — przykładowe wpisy. */
  "Example wall posts": "Przykładowe wpisy na ścianie",
  "Visible to the 4 people in this group. Nobody else, ever.":
    "Widoczne dla 4 osób z tej grupy. Dla nikogo więcej, nigdy.",
  "posted to": "w grupie",
  "Nobody told me the last hour of that walk was straight up":
    "nikt mnie nie uprzedził, że ostatnia godzina tego szlaku to samo pod górę",
  "the water was 9 degrees. nine.": "woda miała 9 stopni. dziewięć.",
  "and you went in twice": "a ty wszedłeś do niej dwa razy",
  "found the good coffee. only took until the last morning":
    "znalazłem tę dobrą kawę. zajęło tylko do ostatniego poranka",
  "you had four": "wypiłeś cztery",
  "the ferry was late so we swam instead. no regrets":
    "prom się spóźnił, więc poszliśmy popływać. zero żalu",
  "best decision of the week": "najlepsza decyzja tygodnia",
  "i still missed the ferry": "i tak nie zdążyłem na prom",
  Like: "Łapka w górę",
  "Remove your like": "Cofnij łapkę w górę",
  Dislike: "Łapka w dół",
  "Remove your dislike": "Cofnij łapkę w dół",
  Comments: "Komentarze",

  /* Cztery działające demonstracje. */
  "What it actually does": "Co to naprawdę potrafi",
  "Four pieces of the real thing, working, right here. No account, no upload, nothing to install.":
    "Cztery kawałki prawdziwej aplikacji, działające tutaj i teraz. Bez konta, bez wysyłania plików, bez instalowania czegokolwiek.",
  "Six phones, four copies of the same shot":
    "Sześć telefonów, cztery kopie tego samego ujęcia",
  "It finds the near-identical ones — not just the exact copies — and lets you keep the one you actually want.":
    "Znajduje te niemal identyczne, nie tylko dokładne duplikaty, i pozwala zostawić to ujęcie, które naprawdę chcesz.",
  "Name someone once": "Podpisz kogoś raz",
  "Your browser finds the faces, on your device. Tap a person and the library narrows to them.":
    "Twarze znajduje Twoja przeglądarka, na Twoim urządzeniu. Dotknij osoby, a biblioteka zawęzi się do jej zdjęć.",
  "A darkroom, not a shelf": "Ciemnia, a nie półka",
  "Drag them. The original is never rewritten — an edit is a recipe stored beside the file.":
    "Pociągnij suwaki. Oryginał nigdy nie zostaje nadpisany — edycja to przepis zapisany obok pliku.",
  "Crop and straighten": "Kadruj i prostuj",
  "Pick a ratio. Undoable forever, because the crop is a number and not a new file.":
    "Wybierz proporcje. Odwracalne na zawsze, bo kadr to liczba, a nie nowy plik.",
  "2 seconds apart": "2 sekundy różnicy",
  "98% alike": "98% podobieństwa",
  "Keep this one": "Zostaw to",
  Kept: "Zostawione",
  "Nothing is deleted until you say so.":
    "Nic nie znika, dopóki tego nie potwierdzisz.",
  Freed: "Zwolniono",
  "The other goes to the trash for 30 days.":
    "Drugie trafia do kosza na 30 dni.",
  "Choose a person": "Wybierz osobę",
  Exposure: "Ekspozycja",
  Contrast: "Kontrast",
  Warmth: "Ciepło",
  "Crop ratio": "Proporcje kadru",
  Any: "Dowolne",

  /* Strona główna — reszta. */
  "Skip to content": "Przejdź do treści",
  "Page sections": "Sekcje strony",
  "At a glance": "W skrócie",
  "What is included": "Co dostajesz",
  "Legal and account": "Dokumenty i konto",
  Feature: "Funkcja",
  "MyFriends.Cloud compared with a cloud drive and a photo service":
    "MyFriends.Cloud w porównaniu z dyskiem w chmurze i serwisem na zdjęcia",

  /* Ściana — dlaczego w ogóle istnieje. */
  "The wall": "Ściana",
  "Social networks used to be small.":
    "Media społecznościowe kiedyś były małe.",
  "They began as a closed circle — one school, one year, one group of friends. Then they grew, and kept growing, until the people watching your life were mostly people who barely know you. At some point everybody started editing themselves for an audience, and the ordinary week stopped being worth posting.":
    "Zaczynały jako zamknięty krąg — jedna szkoła, jeden rocznik, jedna paczka znajomych. Potem rosły i rosły, aż Twoje życie zaczęli oglądać głównie ludzie, którzy ledwo Cię znają. W pewnym momencie wszyscy zaczęli redagować siebie pod publiczność, a zwyczajny tydzień przestał być wart wrzucenia.",
  "The people who matter did not change. They are the same handful who were actually there — in the photograph at two in the morning, in the joke that needs context, on the trip nobody else would care about.":
    "Ludzie, na których naprawdę Ci zależy, się nie zmienili. To wciąż ta sama garstka, która przy tym była — na zdjęciu o drugiej w nocy, w żarcie, który wymaga kontekstu, na wyjeździe, który nikogo innego nie obchodzi.",
  "So MyFriends.Cloud has a wall, and it goes no further than your group. Post the moment, argue about it underneath, keep the photo with the conversation. There is no algorithm, no reach, and nobody watching who was not invited.":
    "Dlatego MyFriends.Cloud ma ścianę, która nie sięga dalej niż Twoja grupa. Wrzuć moment, pokłóćcie się o niego pod spodem, zatrzymaj zdjęcie razem z rozmową. Nie ma algorytmu, nie ma zasięgów i nie patrzy nikt, kto nie został zaproszony.",

  /* Przypisy pod sekcjami. */
  "The exact limits, including how a group can keep an offline copy of its key, are set out in the":
    "Dokładne limity, w tym to, jak grupa może trzymać offline kopię swojego klucza, opisuje",
  "privacy policy": "polityka prywatności",
  "Open it in the browser and add it to your home screen — it behaves like an app, offline cache and all. iPhone, Android, Windows, Mac — one group, everybody in it, nobody left out because of the phone they bought. A native Android app is being built; iOS and desktop come after it.":
    "Otwórz w przeglądarce i dodaj do ekranu głównego — zachowuje się jak aplikacja, z pamięcią offline włącznie. iPhone, Android, Windows, Mac — jedna grupa, wszyscy w środku, nikt nie zostaje z boku przez to, jaki telefon kupił. Natywna aplikacja na Androida właśnie powstaje; iOS i wersja na komputer przyjdą po niej.",
  "Paid plans are not switched on yet and these numbers may still move. The free plan is real, and is what everybody is on today.":
    "Płatne plany nie są jeszcze włączone, a te liczby mogą się jeszcze zmienić. Plan darmowy jest prawdziwy — i to na nim są dzisiaj wszyscy.",

  /* Przykładowa zawartość w makiecie aplikacji. */
  "Croatia 2026": "Chorwacja 2026",
  "Croatia & Italy 2026": "Chorwacja i Włochy 2026",
  Slovenia: "Słowenia",
  "Alps, winter": "Alpy, zima",
  "Kasia’s wedding": "Ślub Kasi",

  /* Tytuł karty i opis, dla czytelnika — nie dla robota. */
  "MyFriends.Cloud — a shared photo library for your friends":
    "MyFriends.Cloud — wspólna biblioteka zdjęć dla Twoich znajomych",
  "A shared photo library and private wall for one group of friends. Everyone’s camera roll in one album, encrypted on your own device. No followers, no feed, no strangers.":
    "Wspólna biblioteka zdjęć i prywatna ściana dla jednej paczki znajomych. Rolki aparatu wszystkich w jednym albumie, szyfrowane na Twoim urządzeniu. Bez obserwujących, bez feedu, bez obcych.",
  Yes: "Tak",
  Partly: "Częściowo",
  No: "Nie",

  /* Platformy. */
  Web: "Przeglądarka",
  Desktop: "Komputer",
  "Available now, every browser": "Dostępne teraz, w każdej przeglądarce",
  "Being built now": "Właśnie powstaje",
  Planned: "W planach",
  "A preview of the app": "Podgląd aplikacji",
  "1.93 GB of 2 GB": "1,93 GB z 2 GB",
  "1.93 GB": "1,93 GB",
  Soon: "Wkrótce",
  "Open it": "Otwórz",

  /* Album: rok i kraje. */
  "Optional. Newest albums are listed first.":
    "Opcjonalnie. Najnowsze albumy są na górze.",
  "That does not look like a year.": "To nie wygląda na rok.",
  "Optional. Counted in your statistics and shown on your map.":
    "Opcjonalnie. Liczy się do statystyk i pojawia się na mapie.",
  "Add another country": "Dodaj kolejny kraj",
  main: "główny",
  "Changes show up for everyone in the group straight away.":
    "Zmiany od razu widzą wszyscy w grupie.",

  /* Osoby: przypisywanie zdjec recznie. */
  "Wrong person? Drag the photo onto whoever it really is.":
    "Zła osoba? Przeciągnij zdjęcie na tę właściwą.",

  /* Mapa na stronie glownej. */
  "The map": "Mapa",
  "Every summer, on one map.": "Każde lato na jednej mapie.",
  "Name the countries when you make an album and the map fills itself in — including the ones a trip only passed through, because an album can name several. Add a country you never made an album for, or stop counting one; it is your map either way.":
    "Wpisz kraje przy tworzeniu albumu, a mapa wypełni się sama — także tymi, przez które wyjazd tylko przejeżdżał, bo album może wskazać kilka. Dodaj kraj, do którego nie ma albumu, albo przestań liczyć któryś; tak czy inaczej to Twoja mapa.",
  "A world map filling in with the countries one group of friends has been to.":
    "Mapa świata wypełniająca się krajami, w których była jedna paczka znajomych.",

  /* Mapa: podpowiedz pod kursorem i sterowanie. */
  "Not been yet": "Jeszcze tu nie byliście",
  "Press to add": "Naciśnij, aby dodać",
  "not counted": "nie liczone",
  "added by hand": "dodane ręcznie",
  more: "więcej",
  "Zoom in": "Przybliż",
  "Zoom out": "Oddal",
  "Reset the map": "Zresetuj mapę",
  "Press a country to see its albums. Scroll to zoom.":
    "Naciśnij kraj, aby zobaczyć jego albumy. Przewiń, aby przybliżyć.",
  "Countries you have been to": "Kraje, w których byliście",
  "Countries you have been to. Press one to add or remove it.":
    "Kraje, w których byliście. Naciśnij, aby dodać lub usunąć.",
  "Press a country to add it, or to stop counting one.":
    "Naciśnij kraj, aby go dodać albo przestać liczyć.",
  "Where you have been": "Gdzie byliście",
  "Edit map": "Edytuj mapę",
  "Countries from your albums.": "Kraje z Twoich albumów.",
  "Couldn’t load albums.": "Nie udało się wczytać albumów.",
  "Forgot your password?": "Nie pamiętasz hasła?",
  "Account": "Konto",
  "Recover your account": "Odzyskaj dostęp do konta",
  "Choose a way to restore your account and photos.": "Wybierz sposób odzyskania dostępu do konta i zdjęć.",
  "Use my recovery file": "Użyj pliku odzyskiwania",
  "Ask a group member": "Poproś członka grupy",
  "Use this unlocked device": "Użyj tego odblokowanego urządzenia",
  "Still have another device that shows your photos? Open Your account on that device and choose Use this unlocked device.": "Masz inne urządzenie, na którym widać Twoje zdjęcia? Otwórz na nim Twoje konto i wybierz Użyj tego odblokowanego urządzenia.",
  "Back to sign in": "Wróć do logowania",
  "The passwords do not match.": "Hasła nie są takie same.",
  "Choose your recovery file first.": "Najpierw wybierz plik odzyskiwania.",
  "Your password has been reset. Sign in with your new password.": "Hasło zostało zmienione. Zaloguj się nowym hasłem.",
  "Your friend's group is restored. For other groups, ask a member using Restore group photos in Your account.": "Dostęp do grupy znajomego został przywrócony. W przypadku innych grup poproś ich członków o pomoc, wybierając Przywróć zdjęcia grupowe w Twoje konto.",
  "Your existing group photos remain accessible.": "Nadal masz dostęp do dotychczasowych zdjęć grupowych.",
  "Two-factor authentication stays enabled. If you just used an authenticator code, wait for the next code before signing in.": "Uwierzytelnianie dwuskładnikowe pozostaje włączone. Jeśli kod został właśnie użyty, poczekaj na następny przed logowaniem.",
  "Use the recovery file you downloaded from Your account. It restores the same encryption key, including access to your existing photos.": "Użyj pliku pobranego z Twoje konto. Przywraca on ten sam klucz szyfrowania i dostęp do dotychczasowych zdjęć.",
  "Choose recovery file": "Wybierz plik odzyskiwania",
  "This device must still be able to open your photos. We will preserve every group key before changing your password.": "To urządzenie musi nadal otwierać Twoje zdjęcia. Przed zmianą hasła zachowamy każdy klucz grupowy.",
  "Ask someone you already know in one of your groups. They must confirm your identity in person or on a call, then approve your request in the group's settings. Email alone cannot approve recovery.": "Poproś znajomą osobę z jednej z Twoich grup. Musi potwierdzić Twoją tożsamość osobiście lub podczas rozmowy, a następnie zatwierdzić prośbę w ustawieniach grupy. Sam adres e-mail nie wystarczy.",
  "Confirm new password": "Powtórz nowe hasło",
  "Keep this page open. Your request expires in 15 minutes. Send the request file to your friend, then compare this fingerprint together:": "Nie zamykaj tej strony. Prośba wygasa po 15 minutach. Wyślij plik znajomemu i wspólnie porównajcie ten odcisk klucza:",
  "Download request again": "Pobierz prośbę ponownie",
  "Choose approval file": "Wybierz plik zatwierdzenia",
  "Approval file selected.": "Wybrano plik zatwierdzenia.",
  "Authenticator code, if enabled": "Kod z aplikacji uwierzytelniającej, jeśli włączono",
  "Create a request for my friend": "Utwórz prośbę dla znajomego",
  "Reset password and restore access": "Zmień hasło i przywróć dostęp",
  "Account recovery": "Odzyskiwanie konta",
  "Download a recovery code before you forget your password. Keep the file somewhere private, such as your password manager. Anyone with this file can recover your account; two-factor authentication is still required when enabled.": "Pobierz kod odzyskiwania, zanim zapomnisz hasła. Przechowuj plik w bezpiecznym miejscu, np. w menedżerze haseł. Osoba z tym plikiem może odzyskać konto; jeśli włączono drugi składnik, nadal jest wymagany.",
  "The code is generated on your device. We store only an encrypted key backup, never the code. Creating another code replaces the previous one.": "Kod powstaje na Twoim urządzeniu. Przechowujemy tylko zaszyfrowaną kopię klucza, nigdy kod. Utworzenie nowego kodu zastępuje poprzedni.",
  "Sign in again to create your encryption key first.": "Zaloguj się ponownie, aby najpierw utworzyć klucz szyfrowania.",
  "Generate and download recovery code": "Utwórz i pobierz kod odzyskiwania",
  "Recovery file downloaded. Move it to a safe place. We cannot show this code again.": "Plik odzyskiwania został pobrany. Przenieś go w bezpieczne miejsce. Nie możemy wyświetlić tego kodu ponownie.",
  "Restore group photos": "Przywróć zdjęcia grupowe",
  "If a group is still locked after account recovery, ask a member who can open its photos. Send them a request and compare the fingerprint together. Repeat for each group that needs restoring.": "Jeśli po odzyskaniu konta grupa jest nadal zablokowana, poproś członka, który otwiera jej zdjęcia. Wyślij prośbę i wspólnie porównajcie odcisk klucza. Powtórz to dla każdej grupy wymagającej przywrócenia.",
  "Download a request for my friend": "Pobierz prośbę dla znajomego",
  "Group access restored.": "Dostęp do grupy został przywrócony.",
  "Open group photos": "Otwórz zdjęcia grupy",
  "Help a member recover access": "Pomóż członkowi odzyskać dostęp",
  "Only help someone you already know. Speak to them in person or on a call and compare the fingerprint on their recovery screen. A matching email address or a message alone is not enough.": "Pomagaj tylko znanej Ci osobie. Porozmawiaj z nią osobiście lub przez telefon i porównaj odcisk klucza na jej ekranie odzyskiwania. Zgodny adres e-mail lub sama wiadomość nie wystarczą.",
  "Open this group's photos on this device before approving recovery.": "Przed zatwierdzeniem odzyskiwania odblokuj zdjęcia tej grupy na tym urządzeniu.",
  "The fingerprints do not match. Ask for a new request.": "Odciski klucza nie są zgodne. Poproś o nową prośbę.",
  "Choose their request file": "Wybierz plik prośby",
  "Approving allows this person to reset their account password and reopen this group's photos. Their other groups need separate help. Two-factor authentication stays enabled.": "Zatwierdzenie pozwala tej osobie zmienić hasło konta i odzyskać zdjęcia tej grupy. Inne grupy wymagają osobnej pomocy. Drugi składnik pozostaje włączony.",
  "Approving restores this group's photos to their current account key. Their password and group role stay unchanged.": "Zatwierdzenie przywraca zdjęcia tej grupy dla obecnego klucza konta. Hasło i rola w grupie nie zmieniają się.",
  "I spoke to this person and the fingerprints match.": "Potwierdzam tożsamość tej osoby w rozmowie i zgodność odcisków klucza.",
  "Approve and download reply": "Zatwierdź i pobierz odpowiedź",
  "Send the approval file back to your friend. They must choose it on the recovery page before the request expires.": "Odeślij znajomemu plik zatwierdzenia. Musi wybrać go na stronie odzyskiwania przed wygaśnięciem prośby.",


  "This device cannot open this group with your current key. Ask a trusted group member to restore access from Your account.": "To urządzenie nie otwiera tej grupy za pomocą obecnego klucza. Poproś zaufanego członka grupy o przywrócenie dostępu w sekcji Twoje konto.",
  "Your photos are encrypted. Unlock them with your password, use a saved recovery file, or ask a trusted group member to help.": "Twoje zdjęcia są zaszyfrowane. Odblokuj je hasłem, użyj zapisanego pliku odzyskiwania lub poproś zaufanego członka grupy o pomoc.",
  "Start with one trusted member from any group you belong to. Their approval lets you reset your password and restores that group's photos.": "Zacznij od jednego zaufanego członka dowolnej swojej grupy. Jego zgoda pozwoli Ci zmienić hasło i przywróci zdjęcia tej grupy.",
  "Recover your account: send the request to one trusted member. Confirm your identity in person or on a call and compare the fingerprint. Email alone cannot approve recovery.": "Odzyskaj konto: wyślij prośbę jednemu zaufanemu członkowi. Potwierdź tożsamość osobiście lub podczas rozmowy i porównajcie odcisk klucza. Sam e-mail nie wystarczy do zatwierdzenia.",
  "Restore other groups: after signing in, open Your account → Restore group photos. You need one member who can open the photos from each locked group, not every member.": "Przywróć pozostałe grupy: po zalogowaniu otwórz Twoje konto → Przywróć zdjęcia grupowe. W każdej zablokowanej grupie potrzebujesz pomocy jednego członka, który otwiera jej zdjęcia — nie wszystkich członków.",
  "One person can help with several groups if they belong to them. Reuse the same photo request; they approve it in each group's settings. Your checklist shows who approved and which groups are available.": "Jedna osoba może pomóc w kilku wspólnych grupach. Użyj tej samej prośby o zdjęcia — znajomy zatwierdza ją w ustawieniach każdej grupy. Lista pokazuje, kto zatwierdził dostęp i które grupy są dostępne.",
  "The approving member's group is restored. After signing in, open Your account → Restore group photos to see all your groups, who approved, and which photos still need help.": "Grupa osoby, która zatwierdziła prośbę, została przywrócona. Po zalogowaniu otwórz Twoje konto → Przywróć zdjęcia grupowe. Zobaczysz wszystkie grupy, osoby zatwierdzające i zdjęcia nadal wymagające pomocy.",
  "Approved by": "Zatwierdzone przez",
  "Access provided by": "Dostęp udostępniony przez",
  "You need one trusted member who can open the photos from each group that needs help. You do not need approval from every member. One person can help with several shared groups.": "W każdej grupie wymagającej pomocy wystarczy jeden zaufany członek, który otwiera jej zdjęcia. Nie potrzebujesz zgody wszystkich członków. Jedna osoba może pomóc w kilku wspólnych grupach.",
  "Download one request and send the same file to those members. They approve it in each group's settings and send back a reply for that group. Import each reply here.": "Pobierz jedną prośbę i wyślij ten sam plik tym osobom. Zatwierdzają go w ustawieniach każdej grupy i odsyłają odpowiedź dla danej grupy. Wczytaj tutaj każdą odpowiedź.",
  "Create a new photo request": "Utwórz nową prośbę o zdjęcia",
  "Download one request for my groups": "Pobierz jedną prośbę dla moich grup",
  "Refresh approvals": "Odśwież zatwierdzenia",
  "Approvals update when you press Refresh approvals. We do not check in the background.": "Zatwierdzenia aktualizują się po naciśnięciu Odśwież zatwierdzenia. Nie sprawdzamy ich w tle.",
  "Keep this page open. The photo request lasts 15 minutes and can restore several groups. Download it again to send the same request to another member.": "Pozostaw tę stronę otwartą. Prośba o zdjęcia jest ważna przez 15 minut i może przywrócić kilka grup. Pobierz ją ponownie, aby wysłać tę samą prośbę kolejnemu członkowi.",
  "Confirm your identity in person or on a call and compare this fingerprint with each helper:": "Potwierdź tożsamość osobiście lub podczas rozmowy i porównaj ten odcisk klucza z każdą osobą pomagającą:",
  "Import a group's reply": "Wczytaj odpowiedź dla grupy",
  "{ready} of {total} groups available": "Dostępne grupy: {ready} z {total}",
  "You have no active groups to restore.": "Nie masz aktywnych grup do przywrócenia.",
  "Group recovery progress": "Postęp przywracania grup",
  "Photos available": "Zdjęcia dostępne",
  "Approved — import a reply to restore photos": "Zatwierdzone — wczytaj odpowiedź, aby przywrócić zdjęcia",
  "Needs help from one group member": "Wymaga pomocy jednego członka grupy",
  "Group access restored. You can import the next group's reply using the same request.": "Dostęp do grupy został przywrócony. Możesz wczytać odpowiedź dla kolejnej grupy, korzystając z tej samej prośby.",
  "Share more groups with this person? Open each group's settings and approve the same request file there. Each approval restores only that group.": "Jesteście razem w kilku grupach? Otwórz ustawienia każdej grupy i zatwierdź tam ten sam plik prośby. Każda zgoda przywraca tylko daną grupę.",
  "Reset by email": "Zresetuj przez e-mail",
  "Email recovery": "Odzyskiwanie przez e-mail",
  "Reset your password by email without waiting for a group member. This restores sign-in only; your old group photos stay locked until you restore their keys.": "Zresetuj hasło przez e-mail bez czekania na członka grupy. Przywrócisz tylko logowanie — stare zdjęcia grupowe pozostaną zablokowane do czasu przywrócenia ich kluczy.",
  "Email recovery must already be enabled for your account. A recovery file or an unlocked device can preserve access to your existing photos instead.": "Odzyskiwanie przez e-mail musi być wcześniej włączone na Twoim koncie. Plik odzyskiwania lub odblokowane urządzenie pozwalają zamiast tego zachować dostęp do dotychczasowych zdjęć.",
  "Send password reset link": "Wyślij link do zmiany hasła",
  "If this account has email recovery enabled, a reset link has been sent to its protected recovery address. Check your inbox and spam folder. The link lasts 15 minutes.": "Jeśli na tym koncie włączono odzyskiwanie przez e-mail, link został wysłany na jego chroniony adres odzyskiwania. Sprawdź skrzynkę i spam. Link jest ważny przez 15 minut.",
  "Reset your password": "Zresetuj hasło",
  "Choose a stronger password.": "Wybierz silniejsze hasło.",
  "Your group memberships remain, but old photos are still locked. Open Your account → Restore group photos to use your recovery file or ask a trusted member from each group.": "Nadal należysz do swoich grup, ale stare zdjęcia są zablokowane. Otwórz Twoje konto → Przywróć zdjęcia grupowe, aby użyć pliku odzyskiwania lub poprosić zaufanego członka każdej grupy o pomoc.",
  "This reset link is missing. Request a new email from the recovery page.": "Brakuje linku do zmiany hasła. Poproś o nową wiadomość na stronie odzyskiwania konta.",
  "This resets sign-in without group approval. It creates a new encryption key, so your old group photos will stay locked.": "Przywrócisz logowanie bez zgody członka grupy. Zostanie utworzony nowy klucz szyfrowania, więc stare zdjęcia grupowe pozostaną zablokowane.",
  "Still have your recovery file or a device that opens your photos? Use those recovery options first to keep access.": "Masz plik odzyskiwania lub urządzenie, które nadal otwiera zdjęcia? Najpierw skorzystaj z tych sposobów, aby zachować dostęp.",
  "Use another recovery option": "Użyj innego sposobu odzyskiwania",
  "I understand that email recovery will not unlock my existing group photos.": "Rozumiem, że odzyskiwanie przez e-mail nie odblokuje moich dotychczasowych zdjęć grupowych.",
  "Reset password and restore sign-in": "Zresetuj hasło i przywróć logowanie",
  "Email recovery lets you reset your password without a group member. It restores sign-in, but cannot open old group photos. Keep a recovery file too.": "Odzyskiwanie przez e-mail pozwala zresetować hasło bez pomocy członka grupy. Przywraca logowanie, ale nie odblokowuje starych zdjęć grupowych. Zachowaj również plik odzyskiwania.",
  "Email recovery enabled for": "Odzyskiwanie przez e-mail włączone dla",
  "Email recovery is not enabled yet.": "Odzyskiwanie przez e-mail nie jest jeszcze włączone.",
  "Changing your account email disables email recovery. Enable it again and confirm the new address.": "Zmiana adresu e-mail konta wyłącza odzyskiwanie przez e-mail. Włącz je ponownie i potwierdź nowy adres.",
  "Confirmation will be sent to": "Potwierdzenie zostanie wysłane na",
  "Enable email recovery": "Włącz odzyskiwanie przez e-mail",
  "Open the confirmation email to enable recovery. Then refresh the status here.": "Otwórz wiadomość z potwierdzeniem, aby włączyć odzyskiwanie. Następnie odśwież tutaj status.",
  "Add an email address to your account first.": "Najpierw dodaj adres e-mail do swojego konta.",
  "Refresh email recovery status": "Odśwież status odzyskiwania przez e-mail",
  "Restore photos with my recovery file": "Przywróć zdjęcia z pliku odzyskiwania",
  "{count} groups restored from your recovery file.": "Grupy przywrócone z pliku odzyskiwania: {count}.",
  "This file did not restore additional groups. Already available groups need no action; ask a member for any groups still locked.": "Ten plik nie przywrócił kolejnych grup. Dostępne grupy nie wymagają działania — w pozostałych poproś członka o pomoc.",
};
